import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Eye,
  Calendar,
  Coins,
  Settings,
  DollarSign,
  AlertCircle,
  Globe,
  Lock,
  Loader2,
  Check,
  ChevronsUpDown,
  Users,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Switch } from "../ui/switch";
import { Separator } from "../ui/separator";
import { toast } from "sonner";
import { Campaign } from "../../App";
import {
  useCreateCampaignsApiCallMutation,
  useGetCampaignsByIdApiCallQuery,
  useGetTokenPriceConversionApiCallQuery,
  useUpdateCampaignsApiCallMutation,
} from "../../services/api/campaigns/campaigns.api";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "../../services/api/auth/auth.type";
import { DASHBOARD_ROUTES } from "../../constants/constant";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { useGetUsersApiCallQuery } from "../../services/api/users/users.api";
import useDebounce from "../../hooks/useDebounce";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "../ui/command";
import { cn } from "../ui/utils";
import { Badge } from "../ui/badge";
import { useGetRefferalsCodeApiCallQuery } from "../../services/api/auth/auth.api";
const CampaignForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [openUserSearch, setOpenUserSearch] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]); // array of user IDs
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]); // for Admin mode: array of code strings
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    tokenName: "Bingold",
    tokenSymbol: "BIGOD",
    contractAddress: "0x11526f93BE5f26220E9c7EACB587EdAEBaa9f627",
    totalPool: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    claimModel: "Completion",
    claimStartDate: "",
    claimStartTime: "",
    claimEndDate: "",
    claimEndTime: "",
    enableClaimWindow: true,
    claimedFrom: "BinGold",
    campaignType: "Public" as "Public" | "Private",
    totalCodes: "",
    privateCodeType: "Kol" as "Kol" | "Admin",
  });
  const isAdminMode =
    formData.campaignType === "Private" && formData.privateCodeType === "Admin";
  const isKOLMode =
    formData.campaignType === "Private" && formData.privateCodeType === "Kol";

  const validateName = (value: string): string => {
    if (!value.trim()) return "Campaign name is required";
    if (value.length < 3) return "Name must be at least 3 characters";
    if (value.length > 100) return "Name must be less than 100 characters";
    if (!/^[a-zA-Z0-9\s\-_]+$/.test(value))
      return "Only letters, numbers, spaces, hyphens, and underscores allowed";
    return "";
  };

  const validateDescription = (value: string): string => {
    if (!value.trim()) return "Description is required";
    if (value.length < 10) return "Description must be at least 10 characters";
    if (value.length > 1000)
      return "Description must be less than 1000 characters";
    return "";
  };

  const validateTokenName = (value: string): string => {
    if (!value.trim()) return "Token name is required";
    if (value.length < 2) return "Token name must be at least 2 characters";
    if (value.length > 50) return "Token name must be less than 50 characters";
    if (!/^[a-zA-Z0-9\s]+$/.test(value))
      return "Only letters, numbers, and spaces allowed";
    return "";
  };

  const validateTokenSymbol = (value: string): string => {
    if (!value.trim()) return "Token symbol is required";
    if (value.length < 2 || value.length > 10)
      return "Symbol must be 2-10 characters";

    return "";
  };

  const validateTotalPool = (value: string): string => {
    if (!value) return "Total pool is required";
    const num = Number(value);
    if (isNaN(num) || num <= 0) return "Must be a positive number";
    if (num > 1000000000000) return "Maximum 1 trillion tokens allowed";
    return "";
  };

  const validateDates = (): string => {
    if (!formData.startDate || !formData.endDate) return "";

    const start = new Date(`${formData.startDate}T${formData.startTime}`);
    const end = new Date(`${formData.endDate}T${formData.endTime}`);

    console.log(start, "start", end, "end");

    if (start >= end) return "End date must be after start date";
    // if (start < new Date()) return "Start date cannot be in the past";

    const diffTime = end.getTime() - start.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    if (diffDays > 365) return "Campaign cannot exceed 1 year";

    return "";
  };
  const [isDraft, setIsDraft] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { data: campaignData } = useGetCampaignsByIdApiCallQuery({
    campaignId: id || "",
  });
  const [createCampaigns] = useCreateCampaignsApiCallMutation();
  const [updateCampaigns] = useUpdateCampaignsApiCallMutation();
  const debouncedSearch = useDebounce(userSearchQuery, 500);

  const {
    data: referralsData,
    isLoading: isReferralsLoading,
    isError: isReferralsError,
  } = useGetRefferalsCodeApiCallQuery({
    search: debouncedSearch || "",
    page: 1,
    limit: 15,
  });

  // Fetch KOLs only + search
  const {
    data: usersData,
    isFetching: isLoadingUsers,
    isError,
  } = useGetUsersApiCallQuery({
    limit: 100,
    search: debouncedSearch || undefined,
    isKol: true,
  });

  const users = usersData?.result?.docs || [];
  const refrral = referralsData?.result?.docs || [];

  console.log("users", users);

  const combineDateTime = (date: string, time: string): string => {
    if (!date || !time) return "";
    const dateTime = `${date}T${time}:00.000Z`;
    return new Date(dateTime).toISOString();
  };

  // Helper function to extract date and time from ISO string
  const extractDateTime = (
    isoString: string
  ): { date: string; time: string } => {
    if (!isoString) return { date: "", time: "" };

    // Extract date and time DIRECTLY from ISO string (no timezone conversion)
    const dateStr = isoString.split("T")[0]; // "2025-10-22"
    const timeStr = isoString.split("T")[1].slice(0, 5); // "22:43"

    return { date: dateStr, time: timeStr };
  };

  const [errors, setErrors] = useState<Record<string, string>>({});
  const validateAllFields = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required field validations
    newErrors.name = validateName(formData.name);
    newErrors.description = validateDescription(formData.description);
    newErrors.tokenName = validateTokenName(formData.tokenName);
    newErrors.tokenSymbol = validateTokenSymbol(formData.tokenSymbol);
    newErrors.totalPool = validateTotalPool(formData.totalPool);
    if (!formData.startTime) newErrors.startTime = "Start time is required";
    if (!formData.endTime) newErrors.endTime = "End time is required";

    if (formData.campaignType === "Private") {
      if (isKOLMode) {
        if (selectedUsers.length === 0)
          newErrors.assignedTo = "At least one KOL must be assigned";
        if (!formData.totalCodes || Number(formData.totalCodes) <= 0)
          newErrors.totalCodes = "Total codes must be greater than 0";
      } else if (isAdminMode) {
        if (selectedCodes.length === 0)
          newErrors.referralCodes =
            "At least one referral code must be selected";
      }
    }
    // Date validations
    const dateError = validateDates();
    if (dateError) newErrors.dateRange = dateError;

    setErrors(newErrors);

    // Check if any errors exist
    return Object.values(newErrors).every((error) => !error);
  };
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleSelectChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const handleSubmit = async (saveAsDraft = false) => {
    const isValid = validateAllFields();

    // IF EDITING OR PUBLISHING - MUST PASS VALIDATION
    if (!isValid) {
      console.log("errorrrrr", errors);
      toast.error("Please fix all validation errors before publishing");
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setIsDraft(saveAsDraft);

    // Validate required fields
    const requiredFields = [
      "name",
      "description",
      "tokenName",
      "tokenSymbol",
      "contractAddress",
      "totalPool",
      "startDate",
      "endDate",
    ];

    const missingFields = requiredFields.filter(
      (field) => !formData[field as keyof typeof formData]
    );

    if (!saveAsDraft && missingFields.length > 0 && !validateAllFields()) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      setIsLoading(false);
      return;
    }

    try {
      const startDateTime = combineDateTime(
        formData.startDate,
        formData.startTime
      );
      const endDateTime = combineDateTime(formData.endDate, formData.endTime);
      const campaignsFromData = {
        name: formData.name,
        description: formData.description,
        startDate: startDateTime,
        endDate: endDateTime,
        tokenName: formData.tokenName,
        tokenSymbol: formData.tokenSymbol,
        contractAddress: formData.contractAddress,
        claimType: formData.claimModel,
        airdropAmount: Number(formData.totalPool),
        claimedFrom: formData.claimedFrom,
        campaignType: isEditing ? undefined : formData.campaignType,
        ...(formData.campaignType === "Private" && {
          privateCodeType: formData?.privateCodeType,
        }),

        ...(formData.campaignType === "Private" &&
          isKOLMode && {
            assignedTo: selectedUsers,
            totalCodes: Number(formData.totalCodes),
            referralCodes: [],
          }),

        ...(formData.campaignType === "Private" &&
          isAdminMode && {
            referralCodes: selectedCodes, // backend must support this field
            assignedTo: [],
            totalCodes: 0,
          }),

        ...(isEditing && { campaignId: id }),
      };
      const data = isEditing
        ? await updateCampaigns(campaignsFromData).unwrap()
        : await createCampaigns(campaignsFromData).unwrap();

      if (data.responseCode === 200) {
        toast.success(data.responseMessage);
        navigate(DASHBOARD_ROUTES.CAMPAIGNS);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!campaignData) return;

    console.log("Campaign data loaded:", campaignData);

    const { date: startDate, time: startTime } = extractDateTime(
      campaignData.result.startDate
    );
    const { date: endDate, time: endTime } = extractDateTime(
      campaignData.result.endDate
    );

    // Set common form fields
    setFormData((prev) => ({
      ...prev,
      name: campaignData.result.name || "",
      description: campaignData.result.description || "",
      tokenName: campaignData.result.tokenName || "",
      tokenSymbol: campaignData.result.tokenSymbol || "",
      contractAddress: campaignData.result.contractAddress || "",
      totalPool: campaignData.result.airdropAmount?.toString() || "",
      startDate,
      startTime,
      endDate,
      endTime,
      claimModel: campaignData.result.claimType || "Completion",
      claimedFrom: campaignData.result.claimedFrom || "BinGold",
      campaignType: campaignData.result.campaignType || "Public",
      totalCodes: campaignData.result.totalCodes?.toString() || "",
    }));

    // Handle Private campaign logic
    if (campaignData.result.campaignType === "Private") {
      if (campaignData.result.assignedTo?.length > 0) {
        // KOL Mode
        const userIds = campaignData.result.assignedTo.map((u: any) =>
          typeof u === "string" ? u : u._id
        );
        setSelectedUsers(userIds);
        setFormData((prev) => ({ ...prev, privateCodeType: "Kol" }));
      } else if (campaignData.result.referralCodes?.length > 0) {
        // Admin Mode: backend sends array of _id strings
        const referralIds = campaignData.result.referralCodes.map((ref: any) =>
          typeof ref === "string" ? ref : ref._id || ref
        );
        setSelectedCodes(referralIds);
        setFormData((prev) => ({
          ...prev,
          privateCodeType: "Admin",
        }));
      }
      // If neither assignedTo nor referralCodes exist, default to Kol (or Admin) as fallback if needed
      // Optional: else setFormData(prev => ({ ...prev, privateCodeType: "Kol" }));
    }
  }, [campaignData]);
  const { data: tokenPriceData } = useGetTokenPriceConversionApiCallQuery();

  const getUSDTBalance = (walletBalance: number) => {
    const usdtPrice =
      tokenPriceData?.result?.data?.find((item: any) => item.coin === "usdt")
        ?.tokenPrice || 0;
    const balance = walletBalance * usdtPrice || 0;
    return balance;
  };

  const ErrorMessage = ({ field }: { field: string }) => {
    if (!errors[field]) return null;
    return (
      <p className="text-sm text-red-600 flex items-center gap-1 mt-1">
        <AlertCircle className="h-4 w-4" />
        {errors[field]}
      </p>
    );
  };

  // Clear selected users when no results are found
  useEffect(() => {
    if (
      !isLoadingUsers &&
      users.length === 0 &&
      userSearchQuery.trim() !== ""
    ) {
      // User typed something, but no KOL matches → clear previous selections
      setSelectedUsers([]);
    }
  }, [isLoadingUsers, users.length, userSearchQuery]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(DASHBOARD_ROUTES.CAMPAIGNS)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEditing ? "Edit Campaign" : "Create New Campaign"}
            </h1>
            <p className="text-gray-600 mt-1">
              {isEditing
                ? "Update your campaign settings and configuration"
                : "Set up a new airdrop campaign for your community"}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* <Button
            variant="outline"
            onClick={() => handleSubmit(true)}
            disabled={isLoading}
          >
            {isLoading && isDraft ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save as Draft
          </Button> */}
          <Button
            onClick={() => handleSubmit(false)}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isLoading && !isDraft ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            {isEditing ? "Update & Publish" : "Save & Publish"}
          </Button>
        </div>
      </div>

      {/* Form */}
      <Card>
        <Tabs defaultValue="basic" className="w-full">
          <CardHeader>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger
                value="basic"
                className="flex items-center space-x-2"
              >
                <Calendar className="h-4 w-4" />
                <span>Basic Information</span>
              </TabsTrigger>
              <TabsTrigger
                value="token"
                className="flex items-center space-x-2"
              >
                <Coins className="h-4 w-4" />
                <span>Token & Rewards</span>
              </TabsTrigger>
              <TabsTrigger
                value="claiming"
                className="flex items-center space-x-2"
              >
                <Settings className="h-4 w-4" />
                <span>Claiming Rules</span>
              </TabsTrigger>
            </TabsList>
          </CardHeader>

          <CardContent className="space-y-6">
            <TabsContent value="basic" className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Campaign Type *</Label>
                    <Select
                      value={formData.campaignType}
                      onValueChange={(value: "Public" | "Private") =>
                        setFormData({ ...formData, campaignType: value })
                      }
                      disabled={isEditing}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Public">
                          <div className="flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            Public - Anyone can join
                          </div>
                        </SelectItem>
                        <SelectItem value="Private">
                          <div className="flex items-center gap-2">
                            <Lock className="h-4 w-4" />
                            Private - Invite only
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Campaign Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      placeholder="e.g., Project X Launch Airdrop"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                    />
                    <ErrorMessage field="name" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="adminRole">Claim From</Label>
                    <Select
                      value={formData.claimedFrom}
                      onValueChange={(value: "BinGold" | "Magnum") =>
                        setFormData((prev) => ({ ...prev, claimedFrom: value }))
                      }
                      required
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="BinGold">BinGold</SelectItem>
                        <SelectItem value="Magnum">Magnum</SelectItem>
                      </SelectContent>
                    </Select>
                    <ErrorMessage field="claimedFrom" />
                  </div>
                  {formData.campaignType === "Private" && (
                    <div className="space-y-2">
                      <Label htmlFor="adminRole">CodeType</Label>
                      <Select
                        value={formData.privateCodeType}
                        onValueChange={(value: "Kol" | "Admin") =>
                          setFormData((prev) => ({
                            ...prev,
                            privateCodeType: value,
                          }))
                        }
                        required
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Kol">Kol</SelectItem>
                          <SelectItem value="Admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                      <ErrorMessage field="privateCodeType" />
                    </div>
                  )}

                  {formData.campaignType === "Private" &&
                    formData.privateCodeType === "Kol" && (
                      <div className="space-y-2">
                        <Label>Total Invite Codes *</Label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.totalCodes}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              totalCodes: e.target.value,
                            })
                          }
                          placeholder="e.g., 500"
                        />

                        {errors.totalCodes && (
                          <p className="text-sm text-red-600">
                            {errors.totalCodes}
                          </p>
                        )}
                      </div>
                    )}
                </div>

                {formData.campaignType === "Private" && (
                  <div className="space-y-2">
                    <Label>
                      {isKOLMode
                        ? "Assign KOL Users *"
                        : "Select Referral Codes *"}
                    </Label>

                    <Popover
                      open={openUserSearch}
                      onOpenChange={setOpenUserSearch}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openUserSearch}
                          className="w-full justify-between h-12"
                        >
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-purple-600" />
                            {isKOLMode ? (
                              selectedUsers.length === 0 ? (
                                <span className="text-gray-500">
                                  Select KOL users...
                                </span>
                              ) : (
                                <span>
                                  {selectedUsers.length} KOL(s) selected
                                </span>
                              )
                            ) : selectedCodes.length === 0 ? (
                              <span className="text-gray-500">
                                Select referral codes...
                              </span>
                            ) : (
                              <span>
                                {selectedCodes.length} code(s) selected
                              </span>
                            )}
                          </div>
                          {(isKOLMode ? isLoadingUsers : isReferralsLoading) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                          )}
                        </Button>
                      </PopoverTrigger>

                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-1">
                        <Command shouldFilter={false}>
                          <CommandInput
                            placeholder={
                              isKOLMode
                                ? "Search KOLs by wallet address..."
                                : "Search referral codes..."
                            }
                            value={userSearchQuery}
                            onValueChange={setUserSearchQuery}
                          />

                          {/* Loading State */}
                          {(isKOLMode
                            ? isLoadingUsers
                            : isReferralsLoading) && (
                            <div className="py-8 text-center">
                              <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                              <p className="mt-2 text-sm text-muted-foreground">
                                Searching...
                              </p>
                            </div>
                          )}

                          {/* No Results */}
                          {(isKOLMode ? isError : isReferralsError) && (
                            <div className="py-8 text-center text-sm text-red-600">
                              Error loading data. Please try again.
                            </div>
                          )}

                          {/* Empty State */}
                          {!(isKOLMode ? isLoadingUsers : isReferralsLoading) &&
                            (isKOLMode ? users : refrral).length === 0 &&
                            userSearchQuery && (
                              <div className="py-8 text-center text-sm text-muted-foreground">
                                No {isKOLMode ? "KOL" : "referral code"} found
                              </div>
                            )}

                          {/* Initial Empty State */}
                          {!(isKOLMode ? isLoadingUsers : isReferralsLoading) &&
                            (isKOLMode ? users : refrral).length === 0 &&
                            !userSearchQuery && (
                              <div className="py-8 text-center text-sm text-muted-foreground">
                                Start typing to search{" "}
                                {isKOLMode ? "KOL users" : "referral codes"}...
                              </div>
                            )}

                          {/* Results List */}
                          {(isKOLMode ? users : refrral).length > 0 && (
                            <CommandGroup className="max-h-64 overflow-auto">
                              {(isKOLMode ? users : refrral).map(
                                (item: any) => {
                                  const id = isKOLMode ? item._id : item._id; // Always use _id for selection
                                  const display = isKOLMode
                                    ? item.walletAddress
                                      ? `${item.walletAddress.slice(
                                          0,
                                          8
                                        )}...${item.walletAddress.slice(-6)}`
                                      : item.email || "No contact"
                                    : item.code; // Show the actual code in UI

                                  const isSelected = isKOLMode
                                    ? selectedUsers.includes(id)
                                    : selectedCodes.includes(item._id); // Compare using _id

                                  return (
                                    <CommandItem
                                      key={item._id}
                                      onSelect={() => {
                                        if (isKOLMode) {
                                          setSelectedUsers((prev) =>
                                            prev.includes(item._id)
                                              ? prev.filter(
                                                  (x) => x !== item._id
                                                )
                                              : [...prev, item._id]
                                          );
                                        } else {
                                          // Store _id, but show code
                                          setSelectedCodes((prev) =>
                                            prev.includes(item._id)
                                              ? prev.filter(
                                                  (x) => x !== item._id
                                                )
                                              : [...prev, item._id]
                                          );
                                        }
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          isSelected
                                            ? "opacity-100"
                                            : "opacity-0"
                                        )}
                                      />
                                      <div className="flex items-center justify-between w-full">
                                        <span className="font-medium">
                                          {display}
                                        </span>
                                        {!isKOLMode && item.status && (
                                          <Badge
                                            variant="outline"
                                            className="text-xs"
                                          >
                                            {item.status}
                                          </Badge>
                                        )}
                                      </div>
                                    </CommandItem>
                                  );
                                }
                              )}
                            </CommandGroup>
                          )}
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {/* Selected Items Summary */}
                    {(selectedUsers.length > 0 || selectedCodes.length > 0) && (
                      <div className="flex items-center justify-between text-sm mt-2">
                        <span className="text-gray-600">
                          {isKOLMode
                            ? `${selectedUsers.length} KOL(s) selected`
                            : `${selectedCodes.length} referral code(s) selected`}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            isKOLMode
                              ? setSelectedUsers([])
                              : setSelectedCodes([])
                          }
                          className="text-red-600 hover:text-red-700 text-xs font-medium"
                        >
                          Clear all
                        </button>
                      </div>
                    )}

                    <ErrorMessage
                      field={isKOLMode ? "assignedTo" : "referralCodes"}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="description">Campaign Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    placeholder="Describe your campaign, its goals, and what participants can expect..."
                    rows={4}
                    value={formData.description}
                    onChange={handleInputChange}
                    maxLength={1000}
                    required
                  />
                  <ErrorMessage field="description" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Campaign Start Date *</Label>
                    <Input
                      id="startDate"
                      name="startDate"
                      type="date"
                      value={formData.startDate || "2023-01-01"}
                      min={new Date().toISOString().split("T")[0]}
                      onChange={handleInputChange}
                      required
                    />
                    <ErrorMessage field="startDate" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="startTime">Start Time *</Label>
                    <Input
                      id="startTime"
                      name="startTime"
                      type="time"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      required
                    />
                    <ErrorMessage field="startTime" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="endDate">Campaign End Date *</Label>
                    <Input
                      id="endDate"
                      name="endDate"
                      type="date"
                      value={formData.endDate}
                      min={formData.startDate}
                      onChange={handleInputChange}
                      required
                    />
                    <ErrorMessage field="endDate" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="endTime">End Time *</Label>
                    <Input
                      id="endTime"
                      name="endTime"
                      type="time"
                      value={formData.endTime}
                      onChange={handleInputChange}
                      required
                    />
                    <ErrorMessage field="endTime" />
                  </div>
                  <ErrorMessage field="dateRange" />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="token" className="space-y-6">
              <div className="grid grid-cols-1 gap-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="tokenName">Token Name *</Label>
                    <Input
                      id="tokenName"
                      name="tokenName"
                      placeholder="e.g., My Project Token"
                      value={formData.tokenName}
                      onChange={handleInputChange}
                      required
                      disabled={formData.claimedFrom === "BinGold"}
                    />
                    <ErrorMessage field="tokenName" />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tokenSymbol">Token Symbol *</Label>
                    <Input
                      id="tokenSymbol"
                      name="tokenSymbol"
                      placeholder="e.g., $PXT"
                      value={formData.tokenSymbol}
                      onChange={handleInputChange}
                      required
                      disabled={formData.claimedFrom === "BinGold"}
                    />
                    <ErrorMessage field="tokenSymbol" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contractAddress">
                    BSC Contract Address *
                  </Label>
                  <Input
                    id="contractAddress"
                    name="contractAddress"
                    placeholder="0x..."
                    value={formData.contractAddress}
                    onChange={handleInputChange}
                    disabled={formData.claimedFrom === "BinGold"}
                    required
                  />
                  <p className="text-sm text-gray-500">
                    This address will be used to interact with your token for
                    distribution
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="totalPool">Total Airdrop Pool *</Label>
                  <Input
                    id="totalPool"
                    name="totalPool"
                    type="number"
                    placeholder="1000000"
                    value={formData.totalPool}
                    onChange={handleInputChange}
                    required
                  />
                  <ErrorMessage field="totalPool" />
                  <p className="text-sm text-gray-500">
                    Total number of tokens allocated for this campaign
                  </p>
                </div>
                {tokenPriceData?.result?.data && formData.totalPool && (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        USDT Value:
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-green-900">
                        $
                        {getUSDTBalance(
                          Number(formData.totalPool)
                        ).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="claiming" className="space-y-6">
              <div className="space-y-6">
                <div className="space-y-4">
                  <Label className="text-base font-medium">
                    Claiming Model *
                  </Label>
                  <RadioGroup
                    value={formData.claimModel}
                    onValueChange={(value: "completion" | "instant") =>
                      setFormData((prev) => ({ ...prev, claimModel: value }))
                    }
                  >
                    <div className="space-y-4">
                      <div className="flex items-start space-x-3 p-4 border rounded-lg">
                        <RadioGroupItem
                          value="Completion"
                          id="Completion"
                          className="mt-1"
                        />
                        <div className="space-y-1">
                          <Label
                            htmlFor="post_campaign"
                            className="font-medium"
                          >
                            Claim After Campaign Ends
                          </Label>
                          <p className="text-sm text-gray-500">
                            Users can only claim their rewards after the
                            campaign end date. This allows you to review
                            participants before distribution.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 p-4 border rounded-lg">
                        <RadioGroupItem
                          value="Instant"
                          id="instant"
                          className="mt-1"
                        />
                        <div className="space-y-1">
                          <Label htmlFor="instant" className="font-medium">
                            Instant Claim
                          </Label>
                          <p className="text-sm text-gray-500">
                            Users can claim their rewards immediately after
                            completing all tasks. Best for high-trust
                            communities.
                          </p>
                        </div>
                      </div>
                    </div>
                  </RadioGroup>
                </div>

                {/* {formData.claimModel === "post_campaign" && (
                  <>
                    <Separator />

                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-base font-medium">
                            Claim Window
                          </Label>
                          <p className="text-sm text-gray-500">
                            Set when users can claim their rewards after
                            campaign ends
                          </p>
                        </div>
                        <Switch
                          checked={formData.enableClaimWindow}
                          onCheckedChange={(checked) =>
                            setFormData((prev) => ({
                              ...prev,
                              enableClaimWindow: checked,
                            }))
                          }
                        />
                      </div>

                      {formData.enableClaimWindow && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-gray-50 rounded-lg">
                          <div className="space-y-2">
                            <Label htmlFor="claimStartDate">
                              Claiming Starts On
                            </Label>
                            <Input
                              id="claimStartDate"
                              name="claimStartDate"
                              type="date"
                              value={formData.claimStartDate}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="claimStartTime">Start Time</Label>
                            <Input
                              id="claimStartTime"
                              name="claimStartTime"
                              type="time"
                              value={formData.claimStartTime}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="claimEndDate">
                              Claiming Ends On
                            </Label>
                            <Input
                              id="claimEndDate"
                              name="claimEndDate"
                              type="date"
                              value={formData.claimEndDate}
                              onChange={handleInputChange}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="claimEndTime">End Time</Label>
                            <Input
                              id="claimEndTime"
                              name="claimEndTime"
                              type="time"
                              value={formData.claimEndTime}
                              onChange={handleInputChange}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )} */}
              </div>
            </TabsContent>
          </CardContent>
        </Tabs>
      </Card>

      {/* Security Notice */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <Settings className="h-3 w-3 text-amber-600" />
            </div>
            <div>
              <h4 className="font-medium text-amber-900 mb-1">
                Security Reminder
              </h4>
              <p className="text-sm text-amber-800">
                Once published, campaigns cannot be deleted, only archived. Make
                sure all information is correct before publishing. You can
                always save as draft to review later.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CampaignForm;
